// The time-and-place picker (BOOKINGS_PLAN.md B7b, B7c, B7d, BE1, BE7, BE9,
// BE14): pure shapes and words over GET bookings/scheduling, so they are unit
// tested without Vuetify. The component (components/bookings/TimeAndPlacePicker.vue)
// lays them out; the forms turn the choice into the request fields the confirm
// endpoints read (pickerRequest). The API's own sentences (day notes, warnings,
// the fallback message, a payer's reason) are shown as they arrive.
import {areaWords, areaWordsFromNames, cap} from '~/utils/areaWords';
import {slotTimeWord} from '~/utils/bookingsMember';
import {formatMinor} from '~/utils/adminActionCopy';

/** The picker's modes: what saving the form does with the choice. */
export const PICKER_MODES = Object.freeze({
  // a suggestion the other side still agrees to: books nothing (a hold at most)
  propose: 'propose',
  // agreeing to the other side's time: books the area (BE7 falls back)
  confirm: 'confirm',
  // accepting a challenge with its suggested time: the same as confirm
  accept: 'accept',
  // a club admin setting the time: books straight away
  set: 'set',
});

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const dayOf = (date) => new Date(`${date}T12:00:00Z`);

/** "Thu 16 Oct" from a yyyy-MM-dd. */
export function dateWord(date) {
  if (!date) return '';
  const d = dayOf(date);
  return `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/** "Thursday" from a yyyy-MM-dd. */
export function weekdayWord(date) {
  const d = dayOf(date);
  return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][d.getUTCDay()];
}

/** "7pm" for an instant in the club's zone (the API's labels use the same form). */
export function timeInZone(iso, zone) {
  if (!iso) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {timeZone: zone || 'UTC', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'})
        .formatToParts(new Date(iso));
    const get = (t) => parts.find((p) => p.type === t)?.value;
    return slotTimeWord(`${get('hour')}:${get('minute')}`);
  } catch {
    return '';
  }
}

/** "7pm–8pm". */
export function timeRangeWord(startsAt, endsAt, zone) {
  const from = timeInZone(startsAt, zone);
  const to = timeInZone(endsAt, zone);
  return to ? `${from}–${to}` : from;
}

/** "45 minutes", "1 hour", "1 hour 30 minutes", "2 hours". */
export function lengthWord(minutes) {
  const m = parseInt(minutes, 10);
  if (!m || m <= 0) return '';
  const h = Math.floor(m / 60);
  const rest = m % 60;
  const hours = h ? `${h} hour${h === 1 ? '' : 's'}` : '';
  const mins = rest ? `${rest} minutes` : '';
  return [hours, mins].filter(Boolean).join(' ');
}

/**
 * A day with no booking hours for the sport's areas: nothing to book, a clock
 * time is picked instead. The API flags a day whose slots have all passed the
 * same way (nothing left to offer), so the note tells them apart.
 */
export function dayOpenPlay(d) {
  return !!d?.openPlay && !/booking needed|matches only/i.test(d?.note ?? '');
}

/** A day with nothing left to pick: every slot has started, or none is free for the whole match. */
export function dayPassed(d) {
  if (!d) return true;
  if (dayOpenPlay(d)) return false;
  return !(d.times ?? []).some((t) => !timeChipDisabled(t));
}

/** The day chips: today first, beyond-window days dashed (B7d), open-play days plain. */
export function pickerDayChips(dayList, todayDate) {
  return (dayList ?? []).map((d) => {
    const dt = dayOf(d.date);
    return {
      date: d.date,
      top: d.date === todayDate ? 'Today' : WEEKDAYS[dt.getUTCDay()],
      num: String(dt.getUTCDate()),
      weekday: WEEKDAYS[dt.getUTCDay()],
      beyond: !!d.beyondWindow,
      opensAt: d.opensAt ?? null,
      openPlay: dayOpenPlay(d),
      passed: dayPassed(d),
      note: d.note ?? null,
      hasTimes: (d.times?.length ?? 0) > 0,
    };
  });
}

/** Whether a time's cells leave nothing to book (only walk-up / open play). */
const onlyOpen = (time) => (time?.areas ?? []).length > 0 && (time.areas ?? []).every((a) => a.status === 'open');

/** "4 available" · "Full" · "Available so far" (beyond the window) · "Open" (nothing to book). X88: "free" read as no charge. */
export function timeChipWord(time) {
  const free = time?.freeCount ?? 0;
  const mine = (time?.areas ?? []).some((a) => a.status === 'mine');
  if (free === 0 && !mine) return onlyOpen(time) ? 'Open' : 'Full';
  if (time?.freeSoFar) return 'Available so far';
  if (mine && free === 0) return 'Yours';
  return `${free} available`;
}

/** A full time can't be picked (the API would refuse every area). The match's own slot can. */
export function timeChipDisabled(time) {
  const free = time?.freeCount ?? 0;
  const mine = (time?.areas ?? []).some((a) => a.status === 'mine');
  return free === 0 && !mine && !onlyOpen(time);
}

/** The place chips for one time: the areas as the club names them (matches-only first, taken or frozen struck), then Any free one · Choose later · Somewhere else. */
export function areaChoices(time, {elsewhere = true} = {}) {
  const cells = (time?.areas ?? []).map((a) => ({
    key: a.areaId,
    place: 'area',
    areaId: a.areaId,
    label: a.name,
    tag: a.status === 'mine' ? (a.label || 'Yours')
        : a.struck ? (a.label || (a.status === 'frozen' ? 'Frozen' : 'Booked'))
            : a.forMatches ? 'for matches'
                : a.status === 'open' ? 'open play' : null,
    struck: !!a.struck,
    disabled: !!a.struck,
    status: a.status,
    forMatches: !!a.forMatches,
    priceMinor: a.priceMinor ?? null,
    endsAt: a.endsAt ?? null,
  }));
  const anyFree = cells.some((c) => c.status === 'free');
  const out = [...cells];
  if (anyFree && cells.filter((c) => c.status === 'free').length > 1) out.push({key: 'any', place: 'any', label: 'Any available one', tag: null, struck: false, disabled: false});
  out.push({key: 'later', place: 'later', label: 'Choose later', tag: null, struck: false, disabled: false});
  if (elsewhere) out.push({key: 'elsewhere', place: 'elsewhere', label: 'Somewhere else', tag: null, struck: false, disabled: false});
  return out;
}

/** The place chips on an open-play day: nothing to book, so every area for the sport, Choose later first. */
export function openPlayChoices(areas, {elsewhere = true} = {}) {
  const out = [{key: 'later', place: 'later', label: 'Choose later', tag: null, struck: false, disabled: false}];
  for (const a of areas ?? []) out.push({key: a.id, place: 'area', areaId: a.id, label: a.name, tag: null, struck: false, disabled: false, status: 'open'});
  if (elsewhere) out.push({key: 'elsewhere', place: 'elsewhere', label: 'Somewhere else', tag: null, struck: false, disabled: false});
  return out;
}

/**
 * Where the picker starts for a time: the match's own booking, then the area
 * preferred by the form (the proposal's area, the fixture's), then by mode:
 * an admin books any free one, a suggestion books nothing (Choose later).
 */
export function defaultPlace(mode, time, preferredAreaId = null) {
  const cells = time?.areas ?? [];
  const mine = cells.find((a) => a.status === 'mine');
  if (mine) return {place: 'area', areaId: mine.areaId};
  const preferred = preferredAreaId ? cells.find((a) => a.areaId === preferredAreaId) : null;
  if (preferred && !preferred.struck && preferred.status !== 'open') return {place: 'area', areaId: preferred.areaId};
  const free = cells.filter((a) => a.status === 'free');
  if (mode === PICKER_MODES.set && free.length) return free.length === 1 ? {place: 'area', areaId: free[0].areaId} : {place: 'any', areaId: null};
  return {place: 'later', areaId: null};
}

/** The cell the choice points at, when it names an area. */
export function chosenCell(time, choice) {
  if (choice?.place !== 'area' || !choice.areaId) return null;
  return (time?.areas ?? []).find((a) => a.areaId === choice.areaId) ?? null;
}

/** The group a booking would land in: the chosen area's, or the first free area's for "Any free one". */
export function chosenGroupId(availability, time, choice) {
  const areas = availability?.areas ?? [];
  const groupOf = (areaId) => areas.find((a) => a.id === areaId)?.groupId ?? null;
  if (choice?.place === 'area' && choice.areaId) return groupOf(choice.areaId);
  if (choice?.place === 'any') {
    const first = (time?.areas ?? []).find((a) => a.status === 'free');
    return first ? groupOf(first.areaId) : null;
  }
  return null;
}

/**
 * R10 L1: the chosen area is open play (or closed) at that time, so there is nothing to book and nobody is charged:
 * the split line and the "charged when you confirm" line are left out.
 */
export function nothingToBook(time, choice) {
  const cell = chosenCell(time, choice);
  return !!cell && (cell.status === 'open' || cell.status === 'closed');
}

export function groupName(availability, groupId) {
  // Grant 2026-11-02: "these courts" / "these courts and boards", never "these playing areas" when a noun is known.
  return (availability?.areas ?? []).find((a) => a.groupId === groupId)?.groupName ?? `these ${areaWords(availability).plural}`;
}

/** B7b: the hold tick shows only when the club allows holds, on a suggestion that names a free area. */
export function showHold(availability, mode, time, choice) {
  if (!availability?.allowHolds || mode !== PICKER_MODES.propose) return false;
  const cell = chosenCell(time, choice);
  // X243 (TEST_FT-R3 L-B): nothing to hold on the court already booked for the match.
  if (alreadyBookedFor(availability, choice, cell)) return false;
  return !!cell && (cell.status === 'free' || cell.status === 'mine');
}

/**
 * X243 (TEST_FT-R3 L-B, the app's alreadyBookedFor): the choice is on the court the match's own confirmed booking
 * already holds for it (the API's This match there), as when a member opens Reschedule on a booked match and changes
 * nothing: no "Match bookings open … we book …" and no "Nothing is booked until … agrees".
 */
export function alreadyBookedFor(availability, choice, cell) {
  const b = availability?.booking;
  return !!b && b.status === 'Confirmed' && choice?.place === 'area' && cell?.status === 'mine' && b.playingAreaId === choice?.areaId;
}

export function holdLabel(areaName, otherName) {
  return `Hold ${areaName} until ${otherName || 'they'} ${otherName ? 'replies' : 'reply'}`;
}

export function holdHelp(otherName) {
  const other = otherName || 'they';
  return `Up to 2 days. Leave it unticked to just suggest the time: nothing is booked, and whatever's still free can be booked when ${other} ${otherName ? 'agrees' : 'agree'}.`;
}

/** The payers (the match's players with accounts) with their price and rules for one group (BE9). */
export function payerOptions(availability, groupId) {
  return (availability?.payers ?? []).map((p) => {
    const g = (p.groups ?? []).find((x) => x.groupId === groupId) ?? null;
    return {
      userId: p.userId,
      name: p.name,
      side: p.side,
      isViewer: !!p.isViewer,
      hasSavedCard: !!p.hasSavedCard,
      // Grant 2026-11-02 ("Admins can book whenever they want, why would it matter if they are picked as the payer?"): a club
      // admin is always a valid payer. The API already sends them no refusal and no limit (MatchBookingPlanner.PayerAllowanceOf);
      // isAdmin keeps an older answer from saying either. Members keep every rule (Grant 2026-10-16).
      isAdmin: !!p.isAdmin,
      canBook: p.isAdmin ? true : (g ? g.canBook !== false : true),
      // BE9: match bookings count towards bookings at once; the payer may be over their limit while allowed to book.
      overLimit: !p.isAdmin && !!g && g.maxActiveBookings != null && (g.activeBookings ?? 0) >= g.maxActiveBookings,
      activeBookings: g?.activeBookings ?? 0,
      maxActiveBookings: g?.maxActiveBookings ?? null,
      reason: g?.reason ?? null,
      priceMinor: g?.priceMinor ?? 0,
      priceLine: g?.priceLine ?? 'Free',
      // R4 NM1: on a split club the API's priceMinor is already their half; fullPriceMinor is the whole (older APIs: null).
      fullPriceMinor: g?.fullPriceMinor ?? null,
    };
  });
}

/** Whether anyone would pay for a booking in this group. */
export function priceApplies(availability, groupId) {
  return payerOptions(availability, groupId).some((p) => p.priceMinor > 0);
}

/**
 * BE9: the payer picker shows when a booking is being made (never for a bare suggestion), one person pays, and there
 * is a choice to make: the group has a price, more than one player could pay, or a player's rules refuse them on the
 * group (over their bookings at once, not allowed) — T2 H2: a refused default payer with no picker was a dead end.
 */
export function showPayer(availability, mode, choice, groupId, time = null) {
  if (!availability || mode === PICKER_MODES.propose) return false;
  if (!['area', 'any'].includes(choice?.place)) return false;
  if (nothingToBook(time, choice)) return false; // R10 L1: open play books nothing, so nobody pays
  if ((availability.matchPayer ?? 'OnePayer') !== 'OnePayer') return false;
  if (!groupId) return false;
  const options = payerOptions(availability, groupId);
  return priceApplies(availability, groupId) || !!availability.payerChoiceNeeded || options.length > 1 || options.some((p) => !p.canBook || p.overLimit);
}

/** "Mia can't book Match tables: … Pick another payer, or choose the playing area later." */
/**
 * X240 (TEST_R100 L-1): the API's warning about a payer's rules (it ends with the way out, PayerTail), which the payer
 * picker already says for the payer chosen (payerRefusal), so the picker shows it once.
 */
export function isPayerWarning(w) {
  // Grant 2026-11-02: the API says the club's own noun ("choose the court later"), so the tail is matched by its shape.
  return /(Pick another payer, or choose the [^.]+ later|Choose the [^.]+ later)\.$/.test(String(w ?? '').trim());
}

export function payerRefusal(payer, availability, groupId) {
  if (!payer || payer.isAdmin) return null; // Grant 2026-11-02: an admin payer is never refused (payerOptions' note)
  const where = groupName(availability, groupId);
  // R6 Low 1 / R7 NL1: the viewer's own row speaks to them, a sentence starts with a capital, and a split club has
  // no other payer to pick.
  const own = !!payer.isViewer;
  const who = own ? 'You' : payer.name;
  const noun = areaWords(availability).noun;
  const tail = (availability?.matchPayer ?? 'OnePayer') === 'Split' ? `Choose the ${noun} later.` : `Pick another payer, or choose the ${noun} later.`;
  if (payer.canBook && payer.overLimit) {
    const n = payer.activeBookings;
    return `${who} already ${own ? 'have' : 'has'} ${n} booking${n === 1 ? '' : 's'} on ${where}. ${tail}`;
  }
  if (payer.canBook) return null;
  const reason = payer.reason ? payer.reason.replace(/\.?$/, '.') : '';
  const why = reason ? ` ${reason.charAt(0).toUpperCase()}${reason.slice(1)}` : '';
  return `${who} can't book ${where}.${why} ${tail}`;
}

/** BE9 Split: each side pays its own half, and every side's actual share is named (R4 NM1). */
export function splitLine(availability, groupId) {
  if ((availability?.matchPayer ?? 'OnePayer') !== 'Split') return null;
  const options = payerOptions(availability, groupId).filter((p) => p.priceMinor > 0);
  if (!options.length) return null;
  const currency = availability?.currency || 'gbp';
  // One payer per side: the first player of each side the API lists (its own default order).
  const sides = [];
  for (const p of options) if (!sides.some((s) => s.side === p.side)) sides.push(p);
  // A newer API quotes the half as priceMinor (fullPriceMinor beside it); an older one quoted the whole price.
  const share = (p) => (p.fullPriceMinor != null ? p.priceMinor : Math.round(p.priceMinor / 2));
  const parts = sides.map((p) => `${formatMinor(share(p), currency, {exact: true})} for ${p.isViewer ? 'you' : p.name}`);
  return `Split between the sides: each side pays half of its own price (${parts.join(', ')})`;
}

/**
 * R6 NM2: on a split club, what confirming a time or "Accept and book" costs the viewer, said before they confirm:
 * "Your £3 (half of £6) is charged to your saved card when you confirm." Null when the viewer pays nothing here.
 */
export function viewerShareConfirmLine(availability, groupId) {
  if ((availability?.matchPayer ?? 'OnePayer') !== 'Split' || !groupId) return null;
  const me = payerOptions(availability, groupId).find((p) => p.isViewer);
  if (!me || me.priceMinor <= 0) return null;
  const currency = availability?.currency || 'gbp';
  const share = me.fullPriceMinor != null ? me.priceMinor : Math.round(me.priceMinor / 2);
  const amount = formatMinor(share, currency, {exact: true}) + (me.fullPriceMinor != null ? ` (half of ${formatMinor(me.fullPriceMinor, currency, {exact: true})})` : '');
  if (!me.canBook) return null;
  return me.hasSavedCard
      ? `Your ${amount} is charged to your saved card when you confirm.`
      : `You pay your ${amount} from My bookings to keep the ${areaWords(availability).noun}.`;
}

/**
 * The beyond-the-window sentence (B7d). X241 (TEST_FT-R2 M1): never for an admin's set time (the schedule dialog's
 * set, an admin's accept counter): the API books it straight away whatever the players' window, so "We'll book it
 * later" would sit beside "…is booked for both sides now".
 */
export function farAheadLine(availability, day, choice, {mode, placeName, cell = null} = {}) {
  if (mode === PICKER_MODES.set) return null;
  if (alreadyBookedFor(availability, choice, cell)) return null;
  if (!day?.beyondWindow || !['area', 'any'].includes(choice?.place)) return null;
  const opens = day.opensAt ? dateWord(new Date(day.opensAt).toISOString().slice(0, 10)) : null;
  const what = placeName || `an available ${areaWords(availability).noun}`;
  const rule = availability?.matchBookingOpens === 'days' && availability?.matchBookingDays
      ? `Match bookings open ${availability.matchBookingDays} days ahead`
      : availability?.matchBookingOpens === 'deadline' ? 'Match bookings open up to the match deadline'
          : 'Match bookings open as far ahead as the players can book';
  const when = opens ? `, so ${weekdayWord(day.date)} ${dayOf(day.date).getUTCDate()} opens on ${opens}` : '';
  const then = mode === PICKER_MODES.propose
      ? `Once the time is agreed and the day opens, we book ${what} for this match, ahead of anyone else, and tell both sides.`
      : `The moment it does, we book ${what} for this match, ahead of anyone else, and tell both sides.`;
  return `${rule}${when}. ${then} If it's gone, we'll book another one that's free for matches.`;
}

/** The open-play sentence: nothing to book on this day. */
export function openPlayLine(availability, date) {
  const club = availability?.clubName || 'the club';
  return `${weekdayWord(date)}s are open play at ${club}: no booking, first come first served. You can still say where you're aiming for, or leave it.`;
}

/**
 * One line under the picker saying what saving does with this choice.
 * `placeName` is the area as the club names it, `otherName` the side that
 * still has to agree.
 */
export function summaryLine(availability, day, time, choice, {mode, otherName, placeName, hold = false} = {}) {
  if (!day || !choice) return null;
  const zone = availability?.timeZone;
  const cell = chosenCell(time, choice);
  const when = time?.startsAt
      ? `${dateWord(day.date)}, ${timeRangeWord(time.startsAt, cell?.endsAt ?? time.areas?.[0]?.endsAt, zone)}`
      : dateWord(day.date);
  const length = availability?.matchLengthWord ? ` Matches are ${availability.matchLengthWord}.` : '';
  const other = otherName || 'the other side';
  const where = placeName ? `${placeName}, ` : '';
  const head = `${where}${when}.${length}`;
  if (dayOpenPlay(day)) return `${head} ${openPlayLine(availability, day.date)}`;
  const place = choice.place;
  const noun = areaWords(availability).noun;
  const named = placeName || `an available ${noun}`;
  // R12 L2: an area that is open play (or closed) at that time books nothing, so agreeing doesn't book it either.
  const idle = place === 'area' && cell && (cell.status === 'open' || cell.status === 'closed') && mode !== PICKER_MODES.propose;
  if (idle) return `${head} ${placeName || cap(`the ${noun}`)} is ${cell.status === 'open' ? 'open play then: nothing to book, first come first served' : 'closed then: nothing is booked'}.`;
  if (mode === PICKER_MODES.set) {
    if (place === 'area' || place === 'any') return `${head} Setting the time books ${named} for both sides and tells them.`;
    if (place === 'elsewhere') return `${head} Played somewhere else: nothing is booked here.`;
    return `${head} Nothing is booked; the ${noun} can be chosen later.`;
  }
  if (mode === PICKER_MODES.propose) {
    if (hold && place === 'area') return `${head} ${placeName} is held until ${other} replies; nothing else is booked.`;
    if (place === 'elsewhere') return `${head} Played somewhere else, so nothing is booked.`;
    return `${head} Nothing is booked until ${other} agrees.`;
  }
  // confirm / accept
  if (place === 'area' || place === 'any') return `${head} Agreeing books ${named} for the match. If it's gone, we book another available one or leave it to choose later.`;
  if (place === 'elsewhere') return `${head} Played somewhere else, so nothing is booked.`;
  return `${head} Nothing is booked; the ${noun} can be chosen later.`;
}

/**
 * The request fields the confirm endpoints read (FixtureScheduleRequest,
 * LadderChallengeRequest, ...SuggestionRequest, ...AcceptRejectRequest):
 * an area, any free one, choose later (no area), somewhere else, a hold, a
 * payer and an admin's one-off length. `areaKey` is `suggestedPlayingAreaId`
 * on a challenge send.
 */
export function pickerRequest(choice, {areaKey = 'playingAreaId', specified = true, suggestedDate = undefined} = {}) {
  const out = {};
  if (suggestedDate !== undefined) out.suggestedDate = suggestedDate;
  else if (choice?.startsAt) out.suggestedDate = choice.startsAt;
  const place = choice?.place ?? 'later';
  out[areaKey] = place === 'area' ? (choice.areaId || null) : null;
  if (specified) out.playingAreaSpecified = true;
  out.anyFreeArea = place === 'any';
  out.somewhereElse = place === 'elsewhere';
  out.holdArea = place === 'area' && !!choice?.hold;
  if (choice?.payerUserId) out.payerUserId = choice.payerUserId;
  if (choice?.durationMinutes != null) out.durationMinutes = choice.durationMinutes;
  // X237: an admin's "Book anyway" on a start that runs past closing (the API checks it is a club admin's). Left out
  // otherwise, so every other request is byte-for-byte as before.
  if (place === 'area' && choice?.allowPastClosing) out.allowPastClosing = true;
  return out;
}

const sameInstant = (a, b) => {
  const ta = a ? new Date(a).getTime() : NaN;
  const tb = b ? new Date(b).getTime() : NaN;
  return !Number.isNaN(ta) && ta === tb;
};

/** The day and time in the response for an instant, when it is one of the offered slots. */
export function findTime(availability, startsAt) {
  for (const day of availability?.dayList ?? []) {
    const time = (day.times ?? []).find((t) => sameInstant(t.startsAt, startsAt));
    if (time) return {day, time};
  }
  return {day: null, time: null};
}

/** The day in the response for a yyyy-MM-dd. */
export function findDay(availability, date) {
  return (availability?.dayList ?? []).find((d) => d.date === date) ?? null;
}

/** The yyyy-MM-dd of an instant in the club's zone. */
export function dateOfInstant(iso, zone) {
  if (!iso) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {timeZone: zone || 'UTC', year: 'numeric', month: '2-digit', day: '2-digit'}).formatToParts(new Date(iso));
    const get = (t) => parts.find((p) => p.type === t)?.value;
    return `${get('year')}-${get('month')}-${get('day')}`;
  } catch {
    return null;
  }
}

/**
 * The accept dialog (SchedAccept): what the challenger's suggested area is
 * doing now. "Still free" · "Held for this match" · "Taken" · "Frozen", or
 * null when the suggestion names no area or the time isn't offered.
 */
export function suggestionStatus(availability, suggestedDate, areaId, areaName = null) {
  if (!availability?.bookingsActive || !suggestedDate) return null;
  const {time} = findTime(availability, suggestedDate);
  const cell = areaId && time ? (time.areas ?? []).find((a) => a.areaId === areaId) : null;
  const name = cell?.name ?? areaName ?? null;
  // X219 (R89 M-3): `unknown` = the suggestion's day or time isn't in the loaded window: nothing is inferred from it.
  if (!cell) return name ? {status: null, word: null, areaName: name, held: false, unknown: true} : null;
  const held = cell.status === 'mine' && /held/i.test(cell.label ?? '');
  const word = cell.status === 'free' ? 'Still free'
      : cell.status === 'mine' ? (held ? 'Held for this match' : 'Booked for this match')
          : cell.status === 'frozen' ? 'Frozen'
              : cell.status === 'taken' ? 'Taken'
                  : cell.status === 'open' ? 'Open play' : null;
  return {status: cell.status, word, areaName: name, held, free: cell.status === 'free' || cell.status === 'mine'};
}

/** The accept dialog's agree label: "Accept and book Table 5" when the suggested area can be booked. */
export function acceptAndBookLabel(status, fallback) {
  if (!status?.areaName || !status.free) return fallback;
  return `Accept and book ${status.areaName}`;
}

/** The note under the suggestion in the accept dialog. */
export function acceptSuggestionNote(status, challengerName) {
  if (!status?.areaName) return null;
  const who = challengerName || 'The challenger';
  if (status.held) return `${status.areaName} is held for this match until you reply.`;
  // X219 (R89 M-3): no cell for the suggestion (its day isn't loaded, or the time isn't a start): say nothing, never "taken".
  if (status.status == null) return null;
  if (status.free) return `${who} only suggested ${status.areaName}, so it isn't held. If it's gone by the time you accept, we book another available one for matches or leave it to choose later.`;
  if (status.status === 'open') return `${status.areaName} is open play then: nothing to book, first come first served.`;
  return `${status.areaName} is ${status.word ? status.word.toLowerCase() : 'taken'} then. Accepting books another available one for matches, or leaves it to choose later.`;
}

/** B7c: the match lengths a competition can pick (minutes). */
export const MATCH_LENGTH_OPTIONS = Object.freeze([30, 45, 60, 75, 90, 105, 120, 150, 180]);

/** "Matches last about 1 hour" · "Matches last about 45 minutes (the sport's default)". */
export function matchLengthLine(minutes, sportDefault) {
  const own = parseInt(minutes, 10);
  if (own > 0) return `Matches last about ${lengthWord(own)}`;
  const fallback = parseInt(sportDefault, 10);
  return fallback > 0 ? `Matches last about ${lengthWord(fallback)} (the sport's default)` : 'Matches last about 1 hour (the default)';
}

/** The settings dialogs: null in the box means "back to the sport's default", which the API reads as 0 (null = unchanged). */
export function matchLengthForSave(minutes) {
  const n = parseInt(minutes, 10);
  return n > 0 ? n : 0;
}

/** The admin's one-off length choices (B7c): the competition's length, twice it, or another number. */
export function matchLengthChoices(baseMinutes) {
  const base = parseInt(baseMinutes, 10) || 60;
  const twice = base * 2;
  return [
    {value: base, label: lengthWord(base)},
    {value: twice, label: lengthWord(twice)},
    {value: 'other', label: 'Other'},
  ];
}

/** The match page's booking row words (SchedBooked). */
export function bookingRowWords(booking) {
  if (!booking) return null;
  const chip = {
    booked: {label: 'Booked', kind: 'ok'},
    queued: {label: 'Booking opens later', kind: 'warn'},
    held: {label: 'Held', kind: 'warn'},
    unpaid: {label: 'Awaiting payment', kind: 'warn'},
    released: {label: 'Released', kind: 'neutral'},
  }[booking.word] ?? {label: booking.status || 'Booked', kind: 'neutral'};
  // R6 NM1: the side that has paid its half of a split booking is waiting on the other side, not asked to pay.
  const chipShown = booking.word === 'unpaid' && booking.viewerSharePaid ? {label: 'Your share is paid', kind: 'ok'} : chip;
  // Grant 2026-11-02: the area's own noun from its name ("Court 2" → court), "playing area" only when the name says nothing.
  const noun = areaWordsFromNames([booking.playingAreaName]).noun;
  return {
    title: booking.playingAreaName || cap(noun),
    sub: booking.line || '',
    label: cap(noun),
    chip: chipShown,
    message: booking.message || `Change the time and the ${noun} moves with it. If the match is cancelled, it's freed.`,
    canPay: !!booking.viewerCanPay,
    payTo: booking.viewerCanPay ? `/profile/bookings?pay=${booking.id}` : null,
  };
}

/** BE11: the meet settings row for its held playing areas: "Table 5 and Table 6, every session" · "None". */
export function heldAreasLine(ids, areas, {needsDuration = false} = {}) {
  const names = (ids ?? []).map((id) => (areas ?? []).find((a) => a.id === id)?.name ?? null).filter(Boolean);
  if (!names.length) return needsDuration ? 'None (give the meet a session length to hold areas)' : 'None';
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `${list}, every session`;
}
